import angular from "angular";

import {
  Calculation,
  Language,
  PensionCalculation,
  PensionCompany,
  Translations,
} from "@salaxy/core";

import {
  SessionService,
  SettingsService,
  UiHelpers,
  EditDialogKnownActions
} from "../../services";


/**
 * Provides methods for profiled Welcome messages to the end user.
 */
export class VarmaBannerController implements angular.IController {
  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "SessionService",
    "SettingsService",
    "UiHelpers",
    "$sce"
  ];


  /** Current calculation */
  public calc: Calculation;

  /** Pension Company*/
  public pensionCompany: PensionCompany;

  /** Varma banner/popup flags */
  private _varmaPensionBanners = {
    isReady: false,
    showGetTyel: false,
    showMoveTyel: false,
    showGetYel: false,
  };

  /** Varma pop up dismissed flags */
  private _dismissed = {
    showGetTyel: false,
    showMoveTyel: false,
    showGetYel: [],
    inStorage: false
  };

  /**
   * Creates a new VarmaBannerController
   * @ignore
   */
  constructor(
    private sessionService: SessionService,
    private settingsService: SettingsService,
    private uiHelpers: UiHelpers,
    private $sce: angular.ISCEService,
  ) { }

  /**
   * Implement IController
   */
  public $onInit() {
    // initialization
  }


  /**
   * Show the popup
   */
  public showVarmaPopup() {
    let dialogData: any = {};

    if (this.varmaPensionBanners.showGetTyel) {
      dialogData = {
        dismiss: () => this.dismiss("tyel"),
        varmaShopLink: this.getVarmaShopLink(),
        title: Translations.get("SALAXY.NG1.VarmaBannerComponent.pensionPopup.tyel.title"),
        message: this.$sce.trustAsHtml(Translations.get("SALAXY.NG1.VarmaBannerComponent.pensionPopup.tyel.html")),
        btnText: Translations.get("SALAXY.NG1.VarmaBannerComponent.pensionPopup.tyel.btnText")
      };
    } else if (this.varmaPensionBanners.showMoveTyel) {
      dialogData = {
        dismiss: () => this.dismiss("moveTyel"),
        varmaShopLink:  this.getVarmaShopLink(),
        title: Translations.get("SALAXY.NG1.VarmaBannerComponent.pensionPopup.moveTyel.title"),
        message: this.$sce.trustAsHtml(Translations.get("SALAXY.NG1.VarmaBannerComponent.pensionPopup.moveTyel.html",  {pensionCompany:  Translations.get("SALAXY.ENUM.PensionCompany." + this.settingsService.current?.calc.pensionContracts[0].company+ ".label") })),
        btnText: Translations.get("SALAXY.NG1.VarmaBannerComponent.pensionPopup.moveTyel.btnText")
      };
    } else if (this.varmaPensionBanners.showGetYel) {
      dialogData = {
        dismiss: () => this.dismiss("yel"),
        varmaShopLink:  this.getVarmaShopLink(),
        title: Translations.get("SALAXY.NG1.VarmaBannerComponent.pensionPopup.yel.title"),
        message: this.$sce.trustAsHtml(Translations.get("SALAXY.NG1.VarmaBannerComponent.pensionPopup.yel.html")),
        btnText: Translations.get("SALAXY.NG1.VarmaBannerComponent.pensionPopup.yel.btnText")
      };
    }
    if (dialogData.dismiss) {
      this.uiHelpers.openEditDialog(
        "salaxy-components/modals/communications/VarmaPopup.html",
        dialogData,
        null,
        "lg").then((result)=> {
          if(result.action == EditDialogKnownActions.Cancel){
            dialogData.dismiss();
          }
        });
    }
  }


  /** Gets the flags that indicate which popup to show. */
  public get varmaPensionBanners(): {
    /** if true, banner is ready to be shown */
    isReady: boolean,
    /** If true, shows popup that offers to get employee pension contract from Varma */
    showGetTyel: boolean,
    /** If true, shows popup that offrs to move employee pension contract from other company to Varma */
    showMoveTyel: boolean,
    /** If true, shows popup that offers to get entrepreneur pension contract from Varma */
    showGetYel: boolean,
  } {
    this._varmaPensionBanners.isReady = !!this.calc && !!this.settingsService.current;
    this._varmaPensionBanners.showGetTyel = this._varmaPensionBanners.isReady && this.isTyelCalculation && !this.dismissed.showGetTyel && (this.settingsService.current?.calc.pensionContracts.length == 0 || this.settingsService.current?.calc.pensionContracts.every((x) => !x.company));
    this._varmaPensionBanners.showMoveTyel = this._varmaPensionBanners.isReady && this.isTyelCalculation && !this.dismissed.showMoveTyel && !this._varmaPensionBanners.showGetTyel && this.settingsService.current?.calc.pensionContracts.every((x) => x.company != PensionCompany.Varma) && this.settingsService.current?.calc.pensionContracts.every((x) => x.company != PensionCompany.None);
    this._varmaPensionBanners.showGetYel = this._varmaPensionBanners.isReady && this.isYelCalculation && this.calc.worker?.employmentId && (this.dismissed.showGetYel.indexOf(this.calc.worker?.employmentId) < 0);
    return this._varmaPensionBanners;
  }

   /** Get link to Varma pension contract shop with language parameter */
   public getVarmaShopLink(): string {
    if (!this.settingsService.current) {
      return "https://vaha.asiointi.varma.fi/vakuutushakemus/?PartnerID=Palkkaus&utm_source=partner&utm_medium=palkkaus";
    }
    const userLang : Language = this.sessionService.session.currentAccount?.language ?? Language.Fi;

    switch (userLang) {
      case Language.Default:
      case Language.Fi:
        return "https://vaha.asiointi.varma.fi/vakuutushakemus/?PartnerID=Palkkaus&utm_source=partner&utm_medium=palkkaus";
      case Language.Sv:
        return "https://vaha.asiointi.varma.fi/vakuutushakemus/?PartnerID=Palkkaus&langcode=sv&utm_source=partner&utm_medium=palkkaus";
      case Language.En:
        return "https://vaha.asiointi.varma.fi/vakuutushakemus/?PartnerID=Palkkaus&langcode=en&utm_source=partner&utm_medium=palkkaus";
    }
  }

  /** Adds dismissed to local storage to prevent the display of the popups again */
  private dismiss(pension: "tyel" | "moveTyel" | "yel") {
    switch (pension) {
      case "yel":
        if (this._dismissed.showGetYel.indexOf(this.calc.worker.employmentId) < 0) {
          this._dismissed.showGetYel.push(this.calc.worker.employmentId);
        }
        break;
      case "tyel":
        {
          this._dismissed.showGetTyel = true;
        }
        break;
      case "moveTyel":
        {
          this._dismissed.showMoveTyel = true;
        }
        break;
    }
    this._dismissed.inStorage = true;
    if (window.localStorage) {
      window.localStorage.setItem("salaxy-varma-banner-dismissed", JSON.stringify(this._dismissed));
    }
  }

  /** Checks if the banner as been dismissed earlier */
  private get dismissed() {
    if (!this._dismissed.inStorage) {

      if (window.localStorage) {
        const json = window.localStorage.getItem("salaxy-varma-banner-dismissed");
        if (json) {
          this._dismissed = JSON.parse(json);
          this._dismissed.inStorage = true;
        }
      }

    }
    return this._dismissed;
  }


  /** Logic for determing the pension is for employee */
  private get isTyelCalculation() {
    if (!this.calc) {
      return false;
    }
    return this.calc.result?.responsibilities?.pensionCalculation == PensionCalculation.Employee;
  }

  /** Logic for determing the pension is for entrepreneur */
  private get isYelCalculation() {
    if (!this.calc) {
      return false;
    }
    return this.calc.result?.responsibilities?.pensionCalculation == PensionCalculation.Entrepreneur;
  }

}
